import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit2, Download, CheckCircle2, XCircle, Clock, Eye, X, Wallet, Filter } from "lucide-react";

export default function AdminStaticUpi() {
  const [tab, setTab] = useState("ids"); // ids | history
  return (
    <div className="space-y-4" data-testid="admin-static-upi">
      <div className="flex items-center gap-2">
        <h2 className="text-2xl font-bold text-white" style={{fontFamily:"Outfit"}}>Static UPI</h2>
        <div className="ml-auto inline-flex rounded-lg bg-slate-900/60 border border-white/5 p-1 text-xs">
          <TabBtn active={tab==="ids"} onClick={()=>setTab("ids")} testid="tab-ids">UPI Settings</TabBtn>
          <TabBtn active={tab==="history"} onClick={()=>setTab("history")} testid="tab-history">Payment History</TabBtn>
        </div>
      </div>
      {tab === "ids" ? <UpiIdsTab/> : <HistoryTab/>}
    </div>
  );
}

function TabBtn({ active, children, onClick, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`px-3 py-1.5 rounded ${active ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}>{children}</button>;
}

/* ─────────────  UPI IDs settings tab  ───────────── */
function UpiIdsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form data OR null

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/static-upi/ids"); setRows(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this UPI ID?")) return;
    try { await api.delete(`/admin/static-upi/ids/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="card p-5">
      <div className="flex items-center gap-3 mb-3">
        <Wallet size={16} className="text-blue-300"/>
        <h3 className="text-white font-semibold">UPI IDs</h3>
        <button onClick={()=>setEditing({})} className="btn-primary !py-1.5 !px-3 ml-auto inline-flex items-center gap-1.5 text-xs" data-testid="upi-id-add-btn">
          <Plus size={12}/> Add UPI ID
        </button>
      </div>
      {loading ? <Loader2 className="animate-spin text-slate-400"/> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400 uppercase text-[10px]">
              <tr>
                <th className="text-left py-2">Holder</th>
                <th className="text-left py-2">UPI ID</th>
                <th className="text-left py-2">Strategy</th>
                <th className="text-left py-2">Range / Exact</th>
                <th className="text-left py-2">Uses</th>
                <th className="text-left py-2">Priority</th>
                <th className="text-left py-2">Status</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">No UPI IDs yet — add your first one.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-white/5" data-testid={`upi-row-${r.id}`}>
                  <td className="py-2">{r.holder_name}</td>
                  <td className="py-2 font-mono">{r.upi_id}</td>
                  <td className="py-2 capitalize">{r.strategy}</td>
                  <td className="py-2 text-slate-400">
                    {r.strategy === "exact" ? `₹${Number(r.exact_amount||0).toLocaleString("en-IN")}`
                     : r.strategy === "range" ? `₹${Number(r.min_amount||0).toLocaleString("en-IN")} – ₹${Number(r.max_amount||0).toLocaleString("en-IN")}`
                     : "—"}
                  </td>
                  <td className="py-2">{r.uses_count || 0}{r.max_uses ? ` / ${r.max_uses}` : ""}</td>
                  <td className="py-2">{r.priority}</td>
                  <td className="py-2">
                    <span className={r.status === "active" ? "text-emerald-300" : "text-slate-500"}>
                      {r.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={()=>setEditing(r)} className="text-cyan-400 hover:text-cyan-300 mr-2" data-testid={`upi-edit-${r.id}`}><Edit2 size={13}/></button>
                    <button onClick={()=>del(r.id)} className="text-rose-400 hover:text-rose-300" data-testid={`upi-delete-${r.id}`}><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing !== null && <UpiIdModal init={editing} onClose={()=>setEditing(null)} onSaved={()=>{ setEditing(null); load(); }}/>}
    </div>
  );
}

function UpiIdModal({ init, onClose, onSaved }) {
  const isNew = !init.id;
  const [form, setForm] = useState({
    holder_name: init.holder_name || "",
    upi_id: init.upi_id || "",
    strategy: init.strategy || "default",
    status: init.status || "active",
    min_amount: init.min_amount || "",
    max_amount: init.max_amount || "",
    exact_amount: init.exact_amount || "",
    max_uses: init.max_uses || "",
    priority: init.priority ?? 50,
    notes: init.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Cast/strip
      payload.priority = Number(payload.priority || 0);
      for (const k of ["min_amount", "max_amount", "exact_amount", "max_uses"]) {
        payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]);
      }
      if (isNew) await api.post("/admin/static-upi/ids", payload);
      else await api.patch(`/admin/static-upi/ids/${init.id}`, payload);
      toast.success(isNew ? "UPI ID added" : "UPI ID updated");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="upi-id-modal">
      <form onSubmit={save} className="card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={18}/></button>
        <h3 className="text-xl font-bold text-white" style={{fontFamily:"Outfit"}}>{isNew ? "Add UPI ID" : "Edit UPI ID"}</h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <F label="Holder name *" v={form.holder_name} on={set("holder_name")} t="upi-modal-holder"/>
          <F label="UPI ID *" v={form.upi_id} on={set("upi_id")} t="upi-modal-upi-id" placeholder="name@bank" mono/>
          <div>
            <label className="label">Strategy *</label>
            <select value={form.strategy} onChange={set("strategy")} className="input w-full" data-testid="upi-modal-strategy">
              <option value="exact">Exact amount</option>
              <option value="range">Amount range</option>
              <option value="usage">Usage limit</option>
              <option value="random">Random choose</option>
              <option value="default">Default fallback</option>
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select value={form.status} onChange={set("status")} className="input w-full" data-testid="upi-modal-status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {form.strategy === "range" && (
            <>
              <F label="Min amount ₹" v={form.min_amount} on={set("min_amount")} type="number" t="upi-modal-min"/>
              <F label="Max amount ₹" v={form.max_amount} on={set("max_amount")} type="number" t="upi-modal-max"/>
            </>
          )}
          {form.strategy === "exact" && (
            <F label="Exact amount ₹" v={form.exact_amount} on={set("exact_amount")} type="number" t="upi-modal-exact" span/>
          )}
          {form.strategy === "usage" && (
            <F label="Max uses" v={form.max_uses} on={set("max_uses")} type="number" t="upi-modal-max-uses" span/>
          )}
          <F label="Priority (0-100)" v={form.priority} on={set("priority")} type="number" t="upi-modal-priority"/>
          <F label="Notes" v={form.notes} on={set("notes")} t="upi-modal-notes"/>
        </div>
        <div className="flex gap-2 mt-5">
          <button type="submit" disabled={saving} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="upi-modal-save">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
            {isNew ? "Add" : "Save changes"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  ), document.body);
}

function F({ label, v, on, t, type = "text", span = false, mono = false, placeholder }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="label">{label}</label>
      <input type={type} value={v} onChange={on} placeholder={placeholder}
        className={`input w-full ${mono ? "font-mono" : ""}`} data-testid={t}/>
    </div>
  );
}

/* ─────────────  Payment history tab  ───────────── */
function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? "/admin/static-upi/payments" : `/admin/static-upi/payments?status=${filter}`;
      const { data } = await api.get(url);
      setRows(data);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (row, status) => {
    try { await api.patch(`/admin/static-upi/payments/${row.id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const del = async (row) => {
    if (!window.confirm(`Delete receipt ${row.receipt_number}? This can't be undone.`)) return;
    try { await api.delete(`/admin/static-upi/payments/${row.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="card p-5">
      <div className="flex items-center gap-3 mb-3">
        <Filter size={13} className="text-slate-400"/>
        {["all","paid","pending","failed"].map(s => (
          <button key={s} onClick={()=>setFilter(s)}
            className={`text-xs px-2.5 py-1 rounded-full ${filter === s ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}
            data-testid={`filter-${s}`}>
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-400">{rows.length} record{rows.length !== 1 && "s"}</span>
      </div>
      {loading ? <Loader2 className="animate-spin text-slate-400"/> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400 uppercase text-[10px]">
              <tr>
                <th className="text-left py-2">Receipt</th>
                <th className="text-left py-2">Customer</th>
                <th className="text-left py-2">Contact</th>
                <th className="text-left py-2">Amount</th>
                <th className="text-left py-2">UPI ID</th>
                <th className="text-left py-2">UTR</th>
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Status</th>
                <th className="text-right py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="text-slate-200">
              {rows.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-slate-500">No payments yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-white/5 align-top" data-testid={`payment-row-${r.id}`}>
                  <td className="py-2 font-mono">{r.receipt_number}</td>
                  <td className="py-2">
                    <div className="text-white">{r.name}</div>
                    <div className="text-[10px] text-slate-500">{r.city}, {r.state} · {r.postal_code}</div>
                  </td>
                  <td className="py-2">
                    <div>{r.email}</div>
                    <div className="text-[10px] text-slate-500">{r.mobile}</div>
                  </td>
                  <td className="py-2 font-semibold">₹{Number(r.amount).toLocaleString("en-IN")}</td>
                  <td className="py-2 font-mono">{r.upi_id}</td>
                  <td className="py-2 font-mono">{r.utr || "—"}</td>
                  <td className="py-2 text-slate-400">{(r.paid_at || r.created_at || "").slice(0,16).replace("T"," ")}</td>
                  <td className="py-2">
                    <StatusPill s={r.status}/>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button onClick={()=>setView(r)} title="View" className="text-cyan-400 hover:text-cyan-300 mr-1.5" data-testid={`payment-view-${r.id}`}><Eye size={13}/></button>
                    <a href={`/api/admin/static-upi/payments/${r.id}/receipt.pdf`} download title="Download PDF" className="text-blue-400 hover:text-blue-300 mr-1.5" data-testid={`payment-pdf-${r.id}`}><Download size={13}/></a>
                    <button onClick={()=>setStatus(r,"paid")} title="Mark paid" className="text-emerald-400 hover:text-emerald-300 mr-1.5" data-testid={`payment-paid-${r.id}`}><CheckCircle2 size={13}/></button>
                    <button onClick={()=>setStatus(r,"pending")} title="Mark pending" className="text-amber-400 hover:text-amber-300 mr-1.5" data-testid={`payment-pending-${r.id}`}><Clock size={13}/></button>
                    <button onClick={()=>setStatus(r,"failed")} title="Mark failed" className="text-orange-400 hover:text-orange-300 mr-1.5" data-testid={`payment-failed-${r.id}`}><XCircle size={13}/></button>
                    <button onClick={()=>del(r)} title="Delete" className="text-rose-400 hover:text-rose-300" data-testid={`payment-delete-${r.id}`}><Trash2 size={13}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {view && <PaymentDetailModal payment={view} onClose={()=>setView(null)}/>}
    </div>
  );
}

function StatusPill({ s }) {
  const map = { paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
                pending: "bg-amber-500/15 text-amber-300 border-amber-500/40",
                failed: "bg-rose-500/15 text-rose-300 border-rose-500/40" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${map[s] || ""}`}>{(s || "").toUpperCase()}</span>;
}

function PaymentDetailModal({ payment, onClose }) {
  return createPortal((
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="payment-detail-modal">
      <div className="card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={18}/></button>
        <h3 className="text-xl font-bold text-white" style={{fontFamily:"Outfit"}}>Payment Details</h3>
        <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
          <D k="Receipt #" v={payment.receipt_number}/>
          <D k="Status" v={<StatusPill s={payment.status}/>}/>
          <D k="Amount" v={`₹${Number(payment.amount).toLocaleString("en-IN")}`}/>
          <D k="UPI ID used" v={payment.upi_id} mono/>
          <D k="UPI holder" v={payment.upi_holder_name}/>
          <D k="UTR / Txn ID" v={payment.utr || "—"} mono/>
          <D k="Created" v={(payment.created_at || "").slice(0,19).replace("T"," ")}/>
          <D k="Paid at" v={(payment.paid_at || "—").slice(0,19).replace("T"," ")}/>
          <D k="Name" v={payment.name} span/>
          <D k="Mobile" v={payment.mobile}/>
          <D k="Email" v={payment.email}/>
          <D k="Address" v={payment.address} span/>
          <D k="City" v={payment.city}/>
          <D k="State" v={payment.state}/>
          <D k="Postal" v={payment.postal_code}/>
          <D k="Country" v={payment.country || "India"}/>
          <D k="Admin notes" v={payment.admin_notes || "—"} span/>
        </div>
        <a href={`/api/admin/static-upi/payments/${payment.id}/receipt.pdf`} download
          className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
          data-testid="payment-detail-pdf">
          <Download size={12}/> Download PDF
        </a>
      </div>
    </div>
  ), document.body);
}

function D({ k, v, span = false, mono = false }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase text-slate-500 tracking-wide">{k}</div>
      <div className={`text-slate-200 mt-0.5 ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
}
